import { ADMIN_API_URL, ADMIN_APP_ID, withAdminAppId } from './apiConfig';
import { getStoredToken } from './authApi';

const AUTH_PREFIX = `${ADMIN_API_URL}`;

async function request(path, options = {}) {
  const token = getStoredToken();
  const res = await fetch(`${AUTH_PREFIX}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...withAdminAppId({ 'X-App-Id': String(ADMIN_APP_ID) }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {}),
    },
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.error ?? json?.message ?? `Request failed: ${res.status}`);
  }
  return json?.data ?? json;
}

function toArray(value, keys = []) {
  if (Array.isArray(value)) return value;
  for (const key of [...keys, 'data', 'result', 'rows', 'items']) {
    if (Array.isArray(value?.[key])) return value[key];
  }
  for (const key of keys) {
    if (Array.isArray(value?.data?.[key])) return value.data[key];
    if (Array.isArray(value?.result?.[key])) return value.result[key];
  }
  return [];
}

function normalizeUser(user) {
  const userId = user.user_id ?? user.userId ?? user.UserId ?? user.legacyUserId ?? user.legacy_user_id ?? user.id ?? user._id;
  const roleId = user.user_type_id ?? user.roleId ?? user.role_id ?? user.RoleId;
  const firstName = user.first_name ?? user.firstName ?? '';
  const lastName = user.last_name ?? user.lastName ?? '';
  const reportingId = user.reporting_id ?? user.reportingId ?? user.reportingManagerId ?? user.managerId ?? 0;
  return {
    ...user,
    user_id: userId,
    userId,
    legacyUserId: user.legacyUserId ?? user.legacy_user_id ?? userId,
    username: user.username ?? user.userName ?? user.user_name ?? `${firstName}${lastName}`.trim(),
    userName: user.userName ?? user.username ?? user.user_name ?? `${firstName}${lastName}`.trim(),
    first_name: firstName,
    firstName,
    last_name: lastName,
    lastName,
    email: user.email,
    mobile: user.mobile ?? user.phoneNumber ?? user.phone_number ?? user.mobile_number,
    mobileNumber: user.mobileNumber ?? user.mobile_number ?? user.mobile ?? user.phoneNumber ?? user.phone_number,
    user_type_id: roleId,
    role_id: roleId,
    roleId,
    reporting_id: reportingId,
    reportingId,
    profile_image: user.profile_image ?? user.photoLocation ?? user.photo_location,
    status: normalizeStatus(user.status ?? user.active_status),
  };
}

function normalizeRole(role) {
  const roleId = role.user_type_id ?? role.role_id ?? role.roleId ?? role.id;
  return {
    ...role,
    user_type_id: roleId,
    user_type_name: role.user_type_name ?? role.role_name ?? role.roleName ?? role.name,
    description: role.description ?? role.role_description ?? role.roleDescription ?? '',
    status: normalizeStatus(role.status ?? role.active_status ?? 'ACTIVE'),
  };
}

function normalizeMenu(menu) {
  const menuId = menu.menu_id ?? menu.menuId ?? menu.legacyMenuId ?? menu.id;
  const parentMenuId = menu.parent_menu_id ?? menu.parentMenuId;
  const menuName = menu.menu_name ?? menu.menuName ?? menu.name ?? menu.label;
  return {
    ...menu,
    menu_id: menuId,
    menu_name: menuName,
    menu_key: menu.menu_key ?? menu.menuKey ?? menu.key,
    api_url: menu.api_url ?? menu.apiUrl ?? menu.route ?? '',
    menu_type: menu.menu_type ?? menu.menuType ?? menu.placement ?? 'MENU',
    parent_menu_id: parentMenuId || null,
    component_name: menu.component_name ?? menu.componentName ?? '',
    display_order: menu.display_order ?? menu.displayOrder ?? menuId ?? 0,
    status: normalizeStatus(menu.status),
  };
}

function normalizeStatus(value) {
  if (value === true || value === 1 || value === '1') return 'ACTIVE';
  const status = String(value ?? 'ACTIVE').trim().toUpperCase();
  return status === 'ACTIVE' || status === 'ENABLED' ? 'ACTIVE' : 'INACTIVE';
}

function numberOrZero(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
}

function idOrZero(value) {
  if (value === undefined || value === null || value === '') return 0;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : value;
}

function integerOrUndefined(value) {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : undefined;
}

function currentScope() {
  return {
    tenantId: localStorage.getItem('tenantId') || undefined,
    businessId: localStorage.getItem('businessId') || undefined,
    businessUnitId: localStorage.getItem('businessUnitId') || undefined,
  };
}

function toUserPayload(user) {
  const username = user.username ?? user.user_name ?? user.userName;
  const firstName = user.first_name ?? user.firstName ?? '';
  const lastName = user.last_name ?? user.lastName ?? '';
  const roleId = numberOrZero(user.user_type_id ?? user.role_id ?? user.roleId);
  const reportingId = idOrZero(user.reporting_id ?? user.reportingId ?? user.managerId);
  const mobile = user.mobile ?? user.mobileNumber ?? user.mobile_number ?? user.phone_number;
  const activeStatus = normalizeStatus(user.status ?? user.activeStatus) === 'ACTIVE';
  const appId = String(ADMIN_APP_ID);
  const updatedBy = integerOrUndefined(user.updated_by ?? user.updatedBy);
  const createdBy = integerOrUndefined(user.created_by ?? user.createdBy);

  return {
    ...user,
    ...currentScope(),
    appId,
    app_id: appId,
    userName: username,
    user_name: username,
    username,
    firstName,
    first_name: firstName,
    lastName,
    last_name: lastName,
    email: user.email,
    user_pwd: user.password ?? user.user_pwd,
    password: user.password ?? user.user_pwd,
    phoneNumber: mobile,
    phone_number: mobile,
    mobileNumber: mobile,
    mobile_number: mobile,
    roleId,
    role_id: roleId,
    user_type_id: roleId,
    reportingId,
    reporting_id: reportingId,
    activeStatus,
    // UserMaster.status is a string in the auth API. Keep activeStatus as the
    // boolean field and send the display/database status using its Go type.
    status: activeStatus ? 'ACTIVE' : 'INACTIVE',
    statusText: activeStatus ? 'ACTIVE' : 'INACTIVE',
    updatedBy,
    updated_by: updatedBy,
    createdBy,
    created_by: createdBy,
  };
}

function toMenuPayload(menu) {
  const menuType = String(menu.menu_type ?? menu.menuType ?? menu.placement ?? 'MENU').toUpperCase();
  return {
    ...menu,
    menuName: menu.menu_name,
    name: menu.menu_name,
    menuKey: menu.menu_key,
    key: menu.menu_key,
    parentMenuId: Number(menu.parent_menu_id ?? 0),
    // MongoDB's menu schema accepts only "main" and "footer". MENU and CHILD
    // are legacy UI types; a child menu's hierarchy is carried by parentMenuId.
    placement: menuType === 'FOOTER' ? 'footer' : 'main',
    icon: menu.icon,
    status: menu.status,
  };
}

function normalizePermissionGroups(groups) {
  const source = Array.isArray(groups)
    ? groups
    : Object.entries(groups ?? {}).map(([menuName, group]) => ({ ...group, menu_name: menuName }));

  return source.map((group) => {
    const menuId = group.menu_id ?? group.menuId;
    const menuName = group.menu_name ?? group.menuName ?? group.name ?? String(menuId ?? '');
    const permissions = Object.entries(group.permissions ?? {}).reduce((acc, [key, permission]) => {
      acc[key] = {
        ...permission,
        permission_id: permission?.permission_id ?? permission?.permissionId ?? permission?.id,
        permission_key: permission?.permission_key ?? permission?.permissionKey ?? key,
        value: permission?.value ?? permission?.access_value ?? permission,
      };
      return acc;
    }, {});
    return {
      ...group,
      menu_id: menuId,
      menu_name: menuName,
      menu_key: group.menu_key ?? group.menuKey ?? menuName,
      permissions,
    };
  });
}

function toPermissionUpdatePayload(roleId, rows, menuOrder = []) {
  const menus = rows.reduce((acc, row) => {
    const menuName = row.menu_name ?? row.menuName ?? row.menu_key ?? String(row.menu_id);
    const permissionKey = row.permission_key ?? row.permissionKey;
    if (!permissionKey) return acc;
    if (!acc[menuName]) {
      acc[menuName] = {
        menuId: row.menu_id ?? row.menuId,
        permissions: {},
      };
    }
    acc[menuName].permissions[permissionKey] = {
      id: row.permission_id ?? row.permissionId,
      value: String(Number(row.access_value ?? row.accessValue) === 1 ? 1 : 0),
    };
    return acc;
  }, {});

  return { roleId, menus, menuOrder };
}

// listUsers — SERVER-side paging and search.
//
// This used to be a hardcoded `?limit=500&offset=0`, with the caller filtering
// and paginating the returned array in the browser. On a database with 6,134
// users that had three compounding effects, all reported as separate bugs:
//   * only the first 500 users existed as far as the UI was concerned;
//   * those 500 are the NEWEST, which here are all provisioned employer logins,
//     so the real staff users were not on any page the admin could reach;
//   * "search" only ever matched inside that same 500, so looking for anyone
//     else returned nothing — the search looked broken when it was simply
//     searching the wrong 500 rows.
//
// The endpoint already supported limit/offset/searchquery/type/sortBy and
// already returned totalCount; nothing here needed a backend change.
//
// Returns { users, total } so the caller can drive real pagination. `total` is
// the server's count for the CURRENT query, which is what a pager needs.
export function listUsers({ limit = 10, offset = 0, search = '', type = '', sort = '' } = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search) params.set('searchquery', search);
  if (type) params.set('type', type);
  if (sort) params.set('sortBy', sort);
  return request(`/get-all-users?${params.toString()}`).then((data) => ({
    users: toArray(data, ['users']).map(normalizeUser),
    total: Number(data?.totalCount ?? data?.total ?? 0) || 0,
  }));
}

export function getUsersFromRole(roleId) {
  return request(`/get-users?roleId=${encodeURIComponent(roleId)}`).then((data) => (
    toArray(data, ['users']).map(normalizeUser)
  ));
}

export function createUser(user) {
  return request('/create-user', {
    method: 'POST',
    body: JSON.stringify(toUserPayload(user)),
  });
}

export function updateUser(userId, user) {
  return request(`/update-user?userId=${encodeURIComponent(userId)}`, {
    method: 'PUT',
    body: JSON.stringify(toUserPayload(user)),
  });
}

export function deleteUser(userId) {
  return request(`/delete-user?userId=${encodeURIComponent(userId)}`, {
    method: 'PUT',
  });
}

export function listUserTypes() {
  return request('/get-roles').then((data) => [
    ...toArray(data, ['zinnext_default']),
    ...toArray(data, ['custom_roles']),
  ].map(normalizeRole));
}

export function createRole(role) {
  return request('/add-custom-role', {
    method: 'POST',
    body: JSON.stringify({
      importedPermissionId: Number(role.imported_permission_id ?? 0),
      newRoleName: role.user_type_name,
      roleDescription: role.description ?? '',
      menuIds: role.visible_menu_ids ?? [],
    }),
  }).then(normalizeRole);
}

export function updateRole(roleId, role) {
  return request(`/custom-roles/${encodeURIComponent(roleId)}`, {
    method: 'PUT',
    body: JSON.stringify({
      roleName: role.user_type_name,
      role_name: role.user_type_name,
      roleDescription: role.description ?? '',
      description: role.description ?? '',
      status: normalizeStatus(role.status) === 'ACTIVE' ? 1 : 0,
      statusText: normalizeStatus(role.status),
    }),
  }).then(normalizeRole);
}

export function deleteRole(roleId) {
  return request(`/custom-roles/${encodeURIComponent(roleId)}`, { method: 'DELETE' });
}

export function listMenus() {
  return request('/admin/menu-modules').then((data) => toArray(data).map(normalizeMenu));
}

export function createMenu(menu) {
  return request('/admin/menu-modules', {
    method: 'POST',
    body: JSON.stringify(toMenuPayload(menu)),
  });
}

export function updateMenu(menuId, menu) {
  return request(`/admin/menu-modules/${menuId}`, {
    method: 'PUT',
    body: JSON.stringify(toMenuPayload(menu)),
  });
}

export function deleteMenu(menuId) {
  return request(`/admin/menu-modules/${menuId}`, {
    method: 'DELETE',
  });
}

export function listMenuPermissions() {
  return request('/admin/menu-actions');
}

export function getRoleMenuPermissions(userTypeId) {
  return request(`/get-role-details?roleid=${encodeURIComponent(userTypeId)}`)
    .then(normalizePermissionGroups);
}

export function saveRoleMenuPermissions(userTypeId, permissions) {
  return request('/edit-role-details', {
    method: 'PUT',
    body: JSON.stringify(toPermissionUpdatePayload(
      userTypeId,
      permissions,
      [...new Set(permissions.map((row) => row.menu_key ?? row.menuName ?? row.menu_name).filter(Boolean))],
    )),
  });
}

export function getUserMenuPermissions(userId) {
  return request(`/get-role-details?userId=${encodeURIComponent(userId)}`)
    .then(normalizePermissionGroups);
}

export function saveUserMenuPermissions(userId, permissions) {
  return request('/edit-role-details', {
    method: 'PUT',
    body: JSON.stringify({ ...toPermissionUpdatePayload(null, permissions), userId }),
  });
}
